import { NativeGradientEditor } from "./native-gradient-editor";
import { inputGradient } from "./native-paint-input";
import type { PaintChannel } from "../native/paint-command";
import type { NativeSession } from "../native/session";
const el = <T extends HTMLElement>(id: string) =>
  document.getElementById(id) as T;
const clamp = (v: number) => Math.min(1, Math.max(0, v));
const collection = [
  "#172033",
  "#FFFFFF",
  "#F3F5F8",
  "#C7CED9",
  "#667085",
  "#111827",
  "#FCA5A5",
  "#FECACA",
  "#FDBA74",
  "#F0DEB7",
  "#FDE68A",
  "#FEF3C7",
  "#86EFAC",
  "#B9DFC4",
  "#BBF7D0",
  "#67E8F9",
  "#BEDEEF",
  "#BAE6FD",
  "#93C5FD",
  "#BFDBFE",
  "#A5B4FC",
  "#C7D2FE",
  "#C4B5FD",
  "#DDD6FE",
  "#F0ABFC",
  "#F5D0FE",
  "#F9A8D4",
  "#FBCFE8",
  "#E85D75",
  "#6D5DFB",
];
export class NativeColorPicker {
  private picker = el("irl-color-picker");
  private pickerHome = this.picker.parentElement!;
  private hostDialog: HTMLDialogElement | null = null;
  private input: HTMLInputElement | null = null;
  private anchor: HTMLElement | null = null;
  private anchorBox: DOMRect | null = null;
  private hue = 0;
  private saturation = 0;
  private value = 0;
  private alpha = 1;
  private drawing = false;
  private pending = false;
  private pendingChannel: PaintChannel = "color";
  private pendingWholeAlpha: number | undefined;
  private selection = "";
  private picking = false;
  private gradient: NativeGradientEditor;
  constructor(
    private session: NativeSession,
    private changed: () => void,
    private sample: (x: number, y: number) => string | null,
  ) {
    this.gradient = new NativeGradientEditor(
      (gradient, commit) => {
        if (!this.input) return;
        this.input.dataset.gradient = JSON.stringify(gradient);
        this.emit(commit, "gradient");
      },
      (stop) => {
        this.load(stop.color);
        this.alpha = stop.alpha;
        this.render();
      },
      (alpha, commit) => this.emit(commit, "alpha", alpha),
    );
    const gradientTab = document.createElement("button");
    gradientTab.type = "button";
    gradientTab.id = "irl-color-gradient-tab";
    gradientTab.className = "irl-color-picker-tab";
    gradientTab.role = "tab";
    gradientTab.dataset.colorTab = "gradient";
    gradientTab.textContent = "Градиент";
    el("irl-color-collection-tab").before(gradientTab);
    this.picker
      .querySelector('[data-color-panel="custom"]')!
      .before(this.gradient.panel);
    document.addEventListener(
      "close",
      (e) => {
        if (e.target === this.hostDialog) this.close();
      },
      true,
    );
    document.addEventListener(
      "cancel",
      (e) => {
        if (e.target === this.hostDialog && this.input) {
          e.preventDefault();
          this.close();
        }
      },
      true,
    );
    document.addEventListener(
      "pointerdown",
      (e) => {
        const target = e.target as HTMLElement;
        if (this.picking && target.id === "native-overlay") {
          e.preventDefault();
          e.stopImmediatePropagation();
          const value = this.sample(e.clientX, e.clientY);
          if (value) {
            this.load(value);
            this.emit(true);
          }
          this.picking = false;
          return;
        }
        const trigger = target.closest<HTMLButtonElement>("[data-color-input]");
        const input = trigger
          ? el<HTMLInputElement>(trigger.dataset.colorInput!)
          : target.closest<HTMLInputElement>('input[type="color"]');
        if (input && !input.disabled) {
          e.preventDefault();
          e.stopPropagation();
          this.open(input, trigger ?? undefined);
          return;
        }
        if (this.input && !this.picker.contains(target)) this.close();
      },
      true,
    );
    document.addEventListener(
      "click",
      (e) => {
        if ((e.target as HTMLElement).matches('input[type="color"]'))
          e.preventDefault();
      },
      true,
    );
    document.addEventListener(
      "keydown",
      (e) => {
        const target = e.target as HTMLElement;
        const trigger = target.closest<HTMLButtonElement>("[data-color-input]");
        const input = trigger
          ? el<HTMLInputElement>(trigger.dataset.colorInput!)
          : target.closest<HTMLInputElement>('input[type="color"]');
        if (input && (e.key === "Enter" || e.key === " ")) {
          e.preventDefault();
          this.open(input, trigger ?? undefined);
        }
        if (this.input && e.key === "Escape") {
          e.preventDefault();
          e.stopImmediatePropagation();
          this.close();
        }
      },
      true,
    );
    for (const button of this.picker.querySelectorAll<HTMLElement>(
      "[data-color-tab]",
    ))
      button.onclick = () => this.tab(button.dataset.colorTab!);
    const grid = el("irl-color-collection-grid");
    for (const color of collection) {
      const b = document.createElement("button");
      b.type = "button";
      b.className = "irl-color-collection-swatch";
      b.dataset.color = color.toLowerCase();
      b.style.background = color;
      b.title = b.ariaLabel = color;
      b.onclick = () => {
        this.load(color);
        this.emit(true);
      };
      grid.append(b);
    }
    this.surface("irl-color-sv", (x, y) => {
      this.saturation = x;
      this.value = 1 - y;
    });
    this.surface("irl-color-hue", (_x, y) => {
      this.hue = y <= 0.002 || y >= 0.998 ? 0 : (360 - y * 360) % 360;
    });
    el("irl-color-sv").onkeydown = (e) => {
      const step = e.shiftKey ? 0.05 : 0.01;
      if (e.key === "ArrowLeft")
        this.saturation = clamp(this.saturation - step);
      else if (e.key === "ArrowRight")
        this.saturation = clamp(this.saturation + step);
      else if (e.key === "ArrowUp") this.value = clamp(this.value + step);
      else if (e.key === "ArrowDown") this.value = clamp(this.value - step);
      else return;
      e.preventDefault();
      e.stopPropagation();
      this.emit(true);
    };
    el("irl-color-hue").onkeydown = (e) => {
      const step = e.shiftKey ? 15 : 3;
      if (!["ArrowUp", "ArrowDown"].includes(e.key)) return;
      this.hue = (this.hue + (e.key === "ArrowUp" ? step : -step) + 360) % 360;
      e.preventDefault();
      e.stopPropagation();
      this.emit(true);
    };
    for (const id of ["irl-color-opacity", "irl-color-opacity-number"]) {
      const input = el<HTMLInputElement>(id);
      input.oninput = () => {
        this.alpha = clamp(Number(input.value) / 100);
        this.emit(false, "alpha");
      };
      input.onchange = () => this.emit(true, "alpha");
    }
    el<HTMLInputElement>("irl-color-hex").onchange = () => {
      const input = el<HTMLInputElement>("irl-color-hex"),
        value = input.value.startsWith("#") ? input.value : "#" + input.value;
      if (/^#[\da-f]{6}$/i.test(value)) {
        this.load(value);
        this.emit(true);
      } else this.render();
    };
    el("irl-color-eyedropper").onclick = async () => {
      const w = window as unknown as {
        EyeDropper?: new () => { open: () => Promise<{ sRGBHex: string }> };
      };
      if (!w.EyeDropper) {
        this.picking = !this.picking;
        return;
      }
      const input = this.input;
      try {
        const value = await new w.EyeDropper().open();
        if (input === this.input) {
          this.load(value.sRGBHex);
          this.emit(true);
        }
      } catch {
        /* User cancelled the browser eyedropper. */
      }
    };
    window.addEventListener("resize", () => {
      if (this.anchor) this.position(this.anchor);
    });
  }
  get openState() {
    return !!this.input;
  }
  private open(input: HTMLInputElement, trigger?: HTMLElement) {
    const source = input.dataset.originalSource
      ? el<HTMLInputElement>(input.dataset.originalSource)
      : input;
    if (this.input !== source) this.close();
    // A compact popup can close on the next palette press. Its persistent
    // property button remains the anchor, including when switching tabs.
    const anchor =
      trigger ??
      input
        .closest(".native-property-anchor")
        ?.querySelector<HTMLButtonElement>(":scope > button") ??
      input;
    this.hostDialog = anchor.closest("dialog");
    if (this.hostDialog) {
      this.hostDialog.append(this.picker);
      this.picker.classList.add("settings-owned-picker");
    }
    this.input = source;
    this.picker.dataset.opaqueOnly = String(
      source.dataset.opaqueOnly === "true",
    );
    this.anchor = anchor;
    this.anchorBox = null;
    if (anchor.dataset.colorInput) anchor.setAttribute("aria-expanded", "true");
    this.selection = this.session.selectedIds.join();
    this.load(source.value);
    this.alpha = Number(
      source.dataset.displayAlpha ?? source.dataset.colorAlpha ?? 1,
    );
    this.gradient.open(inputGradient(source), source.value, this.alpha);
    const gradientTab = el<HTMLButtonElement>("irl-color-gradient-tab");
    gradientTab.disabled = source.dataset.gradientSupported !== "true";
    gradientTab.title = gradientTab.disabled
      ? "Этот параметр выбирает отдельный цвет, а не заливку"
      : "";
    this.tab(inputGradient(source) ? "gradient" : "custom");
    this.picker.hidden = false;
    this.picker.setAttribute("aria-hidden", "false");
    this.render();
    this.position(anchor);
  }
  close() {
    if (this.pending)
      this.emit(true, this.pendingChannel, this.pendingWholeAlpha);
    this.input = null;
    if (this.anchor?.dataset.colorInput)
      this.anchor.setAttribute("aria-expanded", "false");
    this.anchor = null;
    this.anchorBox = null;
    this.hostDialog = null;
    this.picker.classList.remove("settings-owned-picker");
    if (this.picker.parentElement !== this.pickerHome)
      this.pickerHome.append(this.picker);
    this.picking = false;
    this.picker.hidden = true;
    this.picker.setAttribute("aria-hidden", "true");
  }
  private surface(id: string, update: (x: number, y: number) => void) {
    const surface = el(id);
    let pointer: number | null = null;
    const move = (e: PointerEvent) => {
      const b = surface.getBoundingClientRect();
      update(
        clamp((e.clientX - b.left) / Math.max(1, b.width)),
        clamp((e.clientY - b.top) / Math.max(1, b.height)),
      );
      this.emit(false);
    };
    surface.onpointerdown = (e) => {
      if (e.button !== 0) return;
      e.preventDefault();
      pointer = e.pointerId;
      this.drawing = true;
      surface.setPointerCapture(pointer);
      move(e);
    };
    surface.onpointermove = (e) => {
      if (pointer === e.pointerId) move(e);
    };
    const finish = (e: PointerEvent) => {
      if (pointer !== e.pointerId) return;
      pointer = null;
      this.drawing = false;
      this.emit(true);
    };
    surface.onpointerup = finish;
    surface.onpointercancel = finish;
  }
  private emit(
    commit: boolean,
    channel: PaintChannel = "color",
    wholeAlpha?: number,
  ) {
    if (!this.input) return;
    if (
      this.gradient.active &&
      channel !== "gradient" &&
      wholeAlpha === undefined
    ) {
      this.gradient.color(this.hex(), this.alpha, commit);
      return;
    }
    if (channel !== "gradient") {
      this.input.value = this.hex();
      this.input.dataset.colorAlpha = String(wholeAlpha ?? this.alpha);
    }
    this.pending = true;
    this.pendingChannel = channel;
    this.pendingWholeAlpha = wholeAlpha;
    this.input.dataset.colorChannel = channel;
    this.input.dispatchEvent(new Event("input", { bubbles: true }));
    if (channel !== "alpha" && this.input.dataset.displayAlpha !== undefined) {
      const appliedAlpha = Number(this.input.dataset.displayAlpha);
      if (!this.gradient.active) this.alpha = appliedAlpha;
      this.gradient.syncOpacity(appliedAlpha);
    }
    if (commit) {
      this.input.dispatchEvent(new Event("change", { bubbles: true }));
      this.pending = false;
    }
    delete this.input.dataset.colorChannel;
    this.render();
    this.changed();
  }
  private load(color: string) {
    const rgb = [1, 3, 5].map((i) => parseInt(color.slice(i, i + 2), 16) / 255),
      [r, g, b] = rgb as [number, number, number],
      max = Math.max(...rgb),
      min = Math.min(...rgb),
      d = max - min;
    this.hue = d
      ? (max === r
          ? (g - b) / d + (g < b ? 6 : 0)
          : max === g
            ? (b - r) / d + 2
            : (r - g) / d + 4) * 60
      : 0;
    this.saturation = max ? d / max : 0;
    this.value = max;
  }
  private hex() {
    const c = this.value * this.saturation,
      x = c * (1 - Math.abs(((this.hue / 60) % 2) - 1)),
      m = this.value - c,
      parts =
        this.hue < 60
          ? [c, x, 0]
          : this.hue < 120
            ? [x, c, 0]
            : this.hue < 180
              ? [0, c, x]
              : this.hue < 240
                ? [0, x, c]
                : this.hue < 300
                  ? [x, 0, c]
                  : [c, 0, x];
    return (
      "#" +
      parts
        .map((v) =>
          Math.round((v + m) * 255)
            .toString(16)
            .padStart(2, "0"),
        )
        .join("")
    );
  }
  private tab(name: string) {
    this.gradient.active = name === "gradient";
    if (name === "gradient") this.gradient.select();
    else if (this.input) {
      this.load(this.input.value);
      this.alpha = Number(
        this.input.dataset.displayAlpha ?? this.input.dataset.colorAlpha ?? 1,
      );
    }
    this.picker.classList.toggle("irl-gradient-active", this.gradient.active);
    this.picker
      .querySelectorAll<HTMLElement>("[data-color-tab]")
      .forEach((b) => {
        b.classList.toggle("active", b.dataset.colorTab === name);
        b.setAttribute("aria-selected", String(b.dataset.colorTab === name));
      });
    this.picker
      .querySelectorAll<HTMLElement>("[data-color-panel]")
      .forEach(
        (p) =>
          (p.hidden =
            p.dataset.colorPanel !== name &&
            !(name === "gradient" && p.dataset.colorPanel === "custom")),
      );
    this.render();
    if (this.anchor && !this.picker.hidden) this.position(this.anchor);
  }
  private position(input: HTMLElement) {
    const measured = input.getBoundingClientRect();
    if (measured.width && measured.height) this.anchorBox = measured;
    const r = this.anchorBox;
    if (!r) return;
    const b = this.picker.getBoundingClientRect(),
      dock = input.closest(".native-bottom-controls"),
      compact = !!dock;
    let left = compact
        ? r.left + r.width / 2 - b.width / 2
        : r.left - b.width - 10,
      top = compact
        ? Math.min(r.top, dock!.getBoundingClientRect().top) - b.height - 12
        : r.top - 22;
    left = Math.max(12, Math.min(innerWidth - b.width - 12, left));
    top = Math.max(12, Math.min(innerHeight - b.height - 12, top));
    this.picker.style.left = left + "px";
    this.picker.style.top = top + "px";
    this.picker.style.setProperty("--native-picker-left", left + "px");
    this.picker.style.setProperty("--native-picker-top", top + "px");
  }
  render() {
    if (!this.input) return;
    if (this.selection !== this.session.selectedIds.join() && !this.drawing) {
      this.pending = false;
      this.close();
      return;
    }
    const hex = this.hex();
    this.picker.style.setProperty(
      "--irl-picker-hue",
      `hsl(${this.hue},100%,50%)`,
    );
    this.picker.style.setProperty("--irl-picker-color", hex);
    el("irl-color-sv-thumb").style.left = this.saturation * 100 + "%";
    el("irl-color-sv-thumb").style.top = (1 - this.value) * 100 + "%";
    el("irl-color-hue-thumb").style.top = (1 - this.hue / 360) * 100 + "%";
    for (const id of ["irl-color-opacity", "irl-color-opacity-number"])
      if (document.activeElement !== el(id))
        el<HTMLInputElement>(id).value = String(Math.round(this.alpha * 100));
    if (document.activeElement !== el("irl-color-hex") || this.drawing)
      el<HTMLInputElement>("irl-color-hex").value = hex;
    this.picker
      .querySelectorAll<HTMLElement>("[data-color]")
      .forEach((b) => b.classList.toggle("active", b.dataset.color === hex));
  }
}
